import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Search, Edit, Trash2, Eye, EyeOff } from "lucide-react";
import { Pagination } from "antd";
import CustomButton from "../../components/Buttons/CustomButton";
import { useThunk } from "../../hooks/useThunk";
import showToast from "../../utils/toastNotifications";

import ConfirmDeleteModal from "../../components/Modals/ConfirmDeleteModal";
import {
  deleteProducerPack,
  getAllProducerPacksForTable,
  toggleProducerPackStatus,
} from "../../services/thunks/producerPackThunks";
import { deleteFromCloudflare } from "../../services/thunks/cloudFlareThunks";

const ProducerPackManagement = () => {
  const auth = useSelector((state) => state.auth);
  const navigate = useNavigate();

  // Custom Hooks
  const [doGetAllProducerPacks] = useThunk(getAllProducerPacksForTable);
  const [doDeleteProducerPack, isDeletingProducerPack] = useThunk(deleteProducerPack);
  const [doToggleStatus, isTogglingStatus] = useThunk(toggleProducerPackStatus);
  const [doDeleteFromCloudflare, isDeletingFromCloudflare] =
    useThunk(deleteFromCloudflare);

  // Local States and Refs
  const [packs, setPacks] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [packToDelete, setPackToDelete] = useState(null);

  const searchTimeoutRef = useRef(null);
  const previousSearchRef = useRef("");

  const itemsPerPage = 10;

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const searchChanged = searchTerm !== previousSearchRef.current;
    previousSearchRef.current = searchTerm;

    const delay = searchChanged && searchTerm ? 500 : 0;

    searchTimeoutRef.current = setTimeout(() => {
      if (searchChanged && searchTerm && currentPage !== 1) {
        setCurrentPage(1);
        fetchProducerPacks(1, searchTerm);
      } else {
        fetchProducerPacks(currentPage, searchTerm);
      }
    }, delay);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, currentPage]);

  // Scroll to top when this page mounts so header/title is visible
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch (e) {
      // ignore in non-browser environments
    }
  }, []);

  const fetchProducerPacks = async (page = 1, search = "") => {
    const response = await doGetAllProducerPacks({
      token: auth.token,
      page,
      limit: itemsPerPage,
      search: search.trim(),
    });

    if (response.success) {
      setPacks(response.response.data.packs || []);
      setTotalItems(response.response.data.pagination?.totalItems || 0);
    } else {
      setPacks([]);
      setTotalItems(0);
    }
  };

  const handleDelete = (pack) => {
    setPackToDelete(pack);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const promises = [];

      const result = await doDeleteProducerPack({
        token: auth.token,
        id: packToDelete._id,
      });

      if (result.success) {
        if (packToDelete.coverImage?.key) {
          promises.push(
            await doDeleteFromCloudflare({
              key: packToDelete.coverImage.key,
              type: "images",
            })
          );
        }
        await Promise.all(promises);

        // Refresh the current page
        await fetchProducerPacks(currentPage, searchTerm);

        setShowDeleteModal(false);
        setPackToDelete(null);
        showToast("success", "Producer pack deleted successfully");
      } else {
        showToast("error", "Failed to delete producer pack");
      }
    } catch (error) {
      showToast("error", "An error occurred during deletion");
    }
  };

  const handleToggleStatus = async (pack) => {
    const result = await doToggleStatus({
      token: auth.token,
      id: pack._id,
    });

    if (result.success) {
      showToast(
        "success",
        `Producer pack ${pack.isActive ? "deactivated" : "activated"} successfully`
      );
      await fetchProducerPacks(currentPage, searchTerm);
    } else {
      showToast("error", "Failed to toggle producer pack status");
    }
  };

  // Handle pagination change
  const handlePaginationChange = (page, pageSize) => {
    setCurrentPage(page);
  };

  return (
    <div className="min-h-screen p-6 text-white">
      <div className="flex justify-between items-center mb-6">
        <div className="relative flex-1 border-lg border-[#808080] pr-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by title or pack ID"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            className="w-full bg-[#45444433] border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white placeholder-[#808080] focus:outline-none focus:border-cyan-500"
          />
        </div>
        <CustomButton
          useGradientBorder
          rounded="rounded-lg"
          textSize="text-[18px] leading-[24px]"
          padding="py-[10.5px] px-[13.5px]"
          font="font-public-sans"
          border="border-lg"
          type="button"
          width="w-[225px]"
          onClick={() => navigate("/add-producer-pack")}
        >
          + Add New Producer Pack
        </CustomButton>
      </div>

      <div className="bg-gray-800 rounded-[8px] overflow-x-auto border-[1px] border-[#dde1e6]">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[#0D141B]">
              <th className="text-left py-4 px-6 font-[600] font-inter text-[14px] text-[#FFFFFF]">
                ID
              </th>
              <th className="text-left py-4 px-6 font-[600] font-inter text-[14px] text-[#FFFFFF]">
                Title
              </th>
              <th className="text-left py-4 px-6 font-[600] font-inter text-[14px] text-[#FFFFFF]">
                Category
              </th>
              <th className="text-left py-4 px-6 font-[600] font-inter text-[14px] text-[#FFFFFF]">
                DAW
              </th>
              <th className="text-left py-4 px-6 font-[600] font-inter text-[14px] text-[#FFFFFF]">
                Plugin
              </th>
              <th className="text-left py-4 px-6 font-[600] font-inter text-[14px] text-[#FFFFFF]">
                Download Count
              </th>
              <th className="text-left py-4 px-6 font-[600] font-inter text-[14px] text-[#FFFFFF]">
                Release Date
              </th>
              <th className="text-left py-4 px-6 font-[600] font-inter text-[14px] text-[#FFFFFF]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {packs.map((pack, index) => (
              <tr
                key={pack._id}
                className={`border-b border-gray-700 ${
                  index % 2 === 0 ? "bg-[#1E1E1E]" : "bg-[#191919]"
                }`}
              >
                <td className="py-4 px-6 text-[#E2E2E2] text-[14px] font-Public_Sans font-[400]">
                  {pack.packId}
                </td>
                <td className="py-4 px-6 text-[#E2E2E2] text-[14px] font-Public_Sans font-[400]">
                  {pack.title}
                </td>
                <td className="py-4 px-6 text-[#E2E2E2] text-[14px] font-Public_Sans font-[400]">
                  {pack.category?.name}
                </td>
                <td className="py-4 px-6 text-[#E2E2E2] text-[14px] font-Public_Sans font-[400]">
                  {pack.daw || "N/A"}
                </td>
                <td className="py-4 px-6 text-[#E2E2E2] text-[14px] font-Public_Sans font-[400]">
                  {pack.plugin || "N/A"}
                </td>
                <td className="py-4 px-6 text-center align-middle text-[#E2E2E2] text-[14px] font-Public_Sans font-[400]">
                  {pack.downloadCount || "N/A"}
                </td>
                <td className="py-4 px-6 text-[#E2E2E2] text-[14px] font-Public_Sans font-[400]">
                  {new Date(pack.publishedDate).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </td>
                <td className="py-4 px-6">
                  <div className="flex gap-2">
                    <button
                      className="flex items-center justify-center gap-2 px-4 py-1 border border-[#B4C007] text-[#B4C007] rounded-[15px] text-sm w-[90px] hover:bg-[#333300] transition"
                      onClick={() => navigate(`/edit-producer-pack/${pack._id}`)}
                    >
                      <Edit className="w-3 h-3" />
                      Edit
                    </button>
                    <button
                      className="flex items-center justify-center gap-2 px-4 py-1 border border-[#C00707] text-[#C00707] rounded-[15px] text-sm w-[90px] hover:bg-[#330000] transition"
                      onClick={() => handleDelete(pack)}
                    >
                      <Trash2 className="w-3 h-3" />
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "16px",
        }}
      >
        <Pagination
          current={currentPage}
          pageSize={itemsPerPage}
          total={totalItems}
          onChange={handlePaginationChange}
          showSizeChanger={false}
          className="custom-pagination"
        />
      </div>

      {showDeleteModal && (
        <ConfirmDeleteModal
          visible={showDeleteModal}
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
          title="Confirm Deletion"
          content={`Are you sure you want to delete the producer pack "${packToDelete?.title}"?`}
          disabled={isDeletingProducerPack || isDeletingFromCloudflare}
          loading={isDeletingProducerPack || isDeletingFromCloudflare}
        />
      )}
    </div>
  );
};

export default ProducerPackManagement;
